import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const NewsAggregation = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  const filters = [
    { value: 'all', label: 'All News', count: 24 },
    { value: 'bullish', label: 'Bullish', count: 12 },
    { value: 'bearish', label: 'Bearish', count: 6 },
    { value: 'neutral', label: 'Neutral', count: 6 }
  ];

  const newsData = [
    {
      id: 1,
      title: "Bitcoin ETF Sees Record $2.1B Inflows This Week",
      summary: "Institutional investors continue to pour money into Bitcoin ETFs, signaling strong institutional adoption and potential price momentum.",
      source: "CryptoNews",
      timestamp: "2 hours ago",
      sentiment: "bullish",
      sentimentScore: 0.85,
      marketImpact: "high",
      image: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=400&h=200&fit=crop",
      tags: ["Bitcoin", "ETF", "Institutional"]
    },
    {
      id: 2,
      title: "Ethereum Network Upgrade Shows 40% Gas Fee Reduction",
      summary: "The latest Ethereum network optimization has resulted in significantly lower transaction costs, improving user experience and adoption.",
      source: "BlockchainDaily",
      timestamp: "4 hours ago",
      sentiment: "bullish",
      sentimentScore: 0.72,
      marketImpact: "medium",
      image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=200&fit=crop",
      tags: ["Ethereum", "Gas Fees", "Network"]
    },
    {
      id: 3,
      title: "Regulatory Concerns Mount as SEC Reviews DeFi Protocols",
      summary: "The Securities and Exchange Commission is increasing scrutiny on decentralized finance protocols, creating uncertainty in the market.",
      source: "RegulatoryWatch",
      timestamp: "6 hours ago",
      sentiment: "bearish",
      sentimentScore: -0.65,
      marketImpact: "high",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=200&fit=crop",
      tags: ["Regulation", "SEC", "DeFi"]
    },
    {
      id: 4,
      title: "Major Bank Announces Cryptocurrency Custody Services",
      summary: "Traditional financial institution enters crypto space with institutional custody solutions, bridging traditional and digital finance.",
      source: "FinanceToday",
      timestamp: "8 hours ago",
      sentiment: "bullish",
      sentimentScore: 0.68,
      marketImpact: "medium",
      image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=200&fit=crop",
      tags: ["Banking", "Custody", "Institutional"]
    },
    {
      id: 5,
      title: "Altcoin Season Indicators Show Mixed Signals",
      summary: "Technical analysis reveals conflicting indicators for altcoin performance, suggesting a period of consolidation ahead.",
      source: "TechnicalAnalysis",
      timestamp: "12 hours ago",
      sentiment: "neutral",
      sentimentScore: 0.05,
      marketImpact: "low",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=200&fit=crop",
      tags: ["Altcoins", "Technical Analysis", "Market"]
    },
    {
      id: 6,
      title: "Crypto Mining Energy Consumption Drops 15% Globally",
      summary: "Improved mining efficiency and renewable energy adoption contribute to reduced environmental impact of cryptocurrency mining.",
      source: "EcoTech",
      timestamp: "1 day ago",
      sentiment: "bullish",
      sentimentScore: 0.58,
      marketImpact: "low",
      image: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=400&h=200&fit=crop",
      tags: ["Mining", "Environment", "Energy"]
    }
  ];

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return 'text-success bg-success/10';
      case 'bearish': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-error bg-error/10';
      case 'medium': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return 'TrendingUp';
      case 'bearish': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  const filteredNews = selectedFilter === 'all' 
    ? newsData 
    : newsData?.filter(news => news?.sentiment === selectedFilter);

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Market News & Sentiment</h2>
          <p className="text-sm text-muted-foreground">
            Curated cryptocurrency news with AI-powered sentiment analysis
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
          {filters?.map((filter) => (
            <button
              key={filter?.value}
              onClick={() => setSelectedFilter(filter?.value)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-standard flex items-center space-x-2 ${
                selectedFilter === filter?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
              }`}
            >
              <span>{filter?.label}</span>
              <span className="text-xs opacity-75">({filter?.count})</span>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        {filteredNews?.map((news) => (
          <div key={news?.id} className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-standard cursor-pointer">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="lg:w-32 lg:h-20 w-full h-32 flex-shrink-0 overflow-hidden rounded-lg">
                <Image 
                  src={news?.image} 
                  alt={news?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <h3 className="text-base font-semibold text-card-foreground line-clamp-2 flex-1">
                    {news?.title}
                  </h3>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getSentimentColor(news?.sentiment)}`}>
                      <Icon name={getSentimentIcon(news?.sentiment)} size={12} />
                      <span className="capitalize">{news?.sentiment}</span>
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getImpactColor(news?.marketImpact)}`}>
                      {news?.marketImpact} Impact
                    </span>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {news?.summary}
                </p>
                
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{news?.timestamp}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Globe" size={12} />
                      <span>{news?.source}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="BarChart3" size={12} />
                      <span>Score: {Math.abs(news?.sentimentScore)?.toFixed(2)}</span>
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {news?.tags?.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center mt-6">
        <button className="px-6 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-primary-foreground transition-standard">
          Load More News
        </button>
      </div>
    </div>
  );
};

export default NewsAggregation;